import {ReactElement, useEffect, useRef, useState} from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import {TFunction} from 'react-i18next';
import useHasOverflowX from 'hooks/useHasOverflowX';
import {Cable} from 'interfaces/Project';
import formatNumber from 'utils/formatNumber';

type Props = {
  cables: Cable[];
  onCableClick: (cable: Cable) => void;
  onCableEdit: (cable: Cable) => void;
  t: TFunction;
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

export default function CablesTableView({
  cables,
  onCableClick,
  onCableEdit,
  t
}: Props): ReactElement {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  useEffect(() => {
    setPage(0);
  }, [cables]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hasHorizontalScroll = useHasOverflowX(tableContainerRef, [cables]);

  const stickyActionSx = {
    position: 'sticky',
    right: 0,
    backgroundColor: 'background.paper',
    ...(hasHorizontalScroll && {borderLeft: '1px solid', borderLeftColor: 'divider'})
  };

  const paginatedCables = cables.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      <TableContainer ref={tableContainerRef} sx={{maxHeight: 400}}>
        <Table
          stickyHeader
          sx={{
            minWidth: 720,
            '& .MuiTableCell-head': {
              backgroundColor: 'background.paper',
              fontWeight: 700,
              color: 'text.primary'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{minWidth: 200}}>{t('common:designation')}</TableCell>
              <TableCell sx={{minWidth: 200}}>{t('common:commercialBrand')}</TableCell>
              <TableCell sx={{width: 110}}>{t('common:conductingSection')}</TableCell>
              <TableCell sx={{width: 130}}>{t('common:externalDiameter')}</TableCell>
              <TableCell sx={{width: 110}}>{t('common:weightKgByMeter')}</TableCell>
              <TableCell sx={{...stickyActionSx, zIndex: 3}} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCables.map((cable) => (
              <TableRow key={cable.id}>
                <TableCell>{cable.name}</TableCell>
                <TableCell>{cable.brand}</TableCell>
                <TableCell>{cable.conductingSection}</TableCell>
                <TableCell>{formatNumber(cable.diameter)}</TableCell>
                <TableCell>{formatNumber(cable.weight)}</TableCell>
                <TableCell sx={{...stickyActionSx, zIndex: 1}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => onCableClick(cable)}
                    >
                      {t('common:select')}
                    </Button>
                    {cable.isEditable ? (
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          onCableEdit(cable);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={cables.length}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage={t('common:rowsPerPage')}
        labelDisplayedRows={({from, to, count}) => t('common:paginationRows', {from, to, count})}
        sx={{borderTop: '1px solid', borderColor: 'divider'}}
      />
    </Box>
  );
}
